:host {
  display: block;
  width: 100%;
  max-width: 100%;
}

.project-card {
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: var(--background);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: clamp(1rem, -0.765rem + 3.676vw, 1.625rem);
}

.project-card-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  text-decoration: none;
}

/* Title Styles */
.project-title {
  color: var(--text-body);
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin: 0;
}

/* Description Styles */
.project-description {
  color: var(--text-hint);
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-transform: uppercase;
  margin: 0;
}

/* Container Styles */
.carousel-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  background: #f5f5f5;
}

/* Scroll Container - Base Styles */
.carousel-scroll-container {
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE 10+ */

  /* Hide scrollbar for WebKit browsers */
  &::-webkit-scrollbar {
    display: none;
  }
}

/* Native Scroll Controls (Chrome 95+) */
.carousel-scroll-container.native-scroll {
  /* Enable native scroll controls */
  overflow-x: auto;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;

  /* Show native scroll buttons in Chrome */
  &::-webkit-scrollbar {
    display: block;
    height: 12px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.3);

    &:hover {
      background: rgba(0, 0, 0, 0.5);
    }
  }

  /* Chrome 95+ native scroll buttons */
  &::-webkit-scrollbar-button {
    display: block;
    width: 12px;
    height: 12px;
    background: rgba(0, 0, 0, 0.3);

    &:hover {
      background: rgba(0, 0, 0, 0.5);
    }
  }

  &::-webkit-scrollbar-button:start:decrement {
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z'/%3E%3C/svg%3E");
    background-size: 8px;
    background-repeat: no-repeat;
    background-position: center;
  }

  &::-webkit-scrollbar-button:end:increment {
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' fill='white' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z'/%3E%3C/svg%3E");
    background-size: 8px;
    background-repeat: no-repeat;
    background-position: center;
  }
}

/* Slide Styles */
.carousel-slide {
  flex: 0 0 100%;
  scroll-snap-align: start;
  position: relative;
}

.image-wrapper {
  position: relative;
  width: 100%;
  height: 250px;
  overflow: hidden;
  transition: transform 0.2s ease;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;
  }
}

/* Single Image Display */
.project-card.single {
  .single-image {
    .image-wrapper {
      height: 200px;
    }
  }
}

/* Empty State */
.project-card.empty {
  padding: 2rem;
  text-align: center;

  .no-images {
    color: #666;
    font-style: italic;
    margin-top: 1rem;
  }
}

/* Arrow Controls (both native and fallback) */
.carousel-container.fallback,
.carousel-container.native {
  .carousel-arrow {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    background: transparent;
    border: none;
    width: 47px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    color: white;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    svg {
      pointer-events: none;
      width: 13px;
      height: 27px;
    }

    .svg-icon g {
      fill: var(--neutral-0);
    }
  }

  .carousel-arrow-left {
    left: 0px;
  }

  .carousel-arrow-right {
    right: 0px;
  }
}

/* Dot Navigation */
.carousel-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  border-radius: 12px;
}

.carousel-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 0px;
  background: var(--neutral-0);
  opacity: 0.3;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 2px;
  fill: var(--neutral-0);

  &:hover {
    border-color: var(--neutral-0);
  }

  &.active {
    border-color: var(--neutral-0);
    opacity: 0.9;
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .image-wrapper {
    height: 200px;
  }

  .carousel-container.fallback {
    .carousel-arrow {
      width: 36px;
      height: 36px;
    }

    .carousel-arrow-left {
      left: 8px;
    }

    .carousel-arrow-right {
      right: 8px;
    }
  }
}

@media (max-width: 480px) {
  .image-wrapper {
    height: 180px;
  }

  .carousel-dot {
    width: 6px;
    height: 6px;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .carousel-arrow {
    background: white;
    border: 2px solid black;
    color: black;
  }

  .carousel-dot {
    border-color: black;

    &.active {
      background: black;
    }
  }

  .project-card {
    border: 1px solid #333;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .carousel-scroll-container {
    scroll-behavior: auto;
  }

  .carousel-arrow,
  .carousel-dot,
  .image-wrapper,
  .image-wrapper img {
    transition: none;
  }

  .image-wrapper:hover img {
    transform: none;
  }
}